/**
 * Job Scheduler Node Factories
 *
 * Creates explorer tree nodes for the job-scheduler section.
 * Uses the engine config to determine labels and availability.
 */

import { createNodeId, createConnectionNodeId } from '../utils/nodeIdUtils';
import { getJobSchedulerEngineConfig } from '../../workspace/job-scheduler/engineConfig';
import type { AgentJobsFolderNode, AgentJobsSubfolderNode } from './nodeTypes';
import type { ContextMenuAction } from './TreeModel';
import type { DatabaseEngineId } from '@/core/engines';
import { isBetaFeatureEnabled } from '../../../core/beta';

const AGENT_FOLDER_CONTEXT_MENU: ContextMenuAction[] = [
  { id: 'open-job-scheduler', label: 'Open Job Scheduler' },
];

const SUBFOLDER_CONTEXT_MENU: ContextMenuAction[] = [
  { id: 'open-job-scheduler', label: 'Open Job Scheduler' },
];

function createAgentFolderNodeId(connectionId: string): string {
  return createNodeId('agent', connectionId);
}

function createAgentSubfolderNodeId(connectionId: string, name: string): string {
  return createNodeId('agent', connectionId, name);
}

/**
 * Creates job-scheduler explorer folders for the given engine.
 * Returns empty array if the engine has no job-scheduler support.
 */
export function createJobSchedulerFolders(
  connectionId: string,
  engine?: DatabaseEngineId | string,
): AgentJobsFolderNode[] {
  const config = getJobSchedulerEngineConfig(engine);
  if (!config) return [];
  // Agent Jobs is an opt-in beta feature — with it off the whole subtree stays
  // out of the Object Explorer, which also takes its context menus (the only
  // other way into the job-scheduler workspace) with it.
  if (!isBetaFeatureEnabled('job-scheduler')) return [];

  const agentFolder: AgentJobsFolderNode = {
    id: createAgentFolderNodeId(connectionId),
    parentId: createConnectionNodeId(connectionId),
    name: config.explorerLabel,
    type: 'agent-jobs-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: { connectionId },
    contextMenuActions: AGENT_FOLDER_CONTEXT_MENU,
  };

  const subfolders: AgentJobsSubfolderNode[] = [
    { id: createAgentSubfolderNodeId(connectionId, 'jobs'), parentId: agentFolder.id, name: 'Jobs', type: 'agent-jobs-subfolder', canHaveChildren: true, children: [], isExpanded: false, metadata: { connectionId }, contextMenuActions: SUBFOLDER_CONTEXT_MENU },
    { id: createAgentSubfolderNodeId(connectionId, 'alerts'), parentId: agentFolder.id, name: 'Alerts', type: 'agent-jobs-subfolder', canHaveChildren: true, children: [], isExpanded: false, metadata: { connectionId }, contextMenuActions: SUBFOLDER_CONTEXT_MENU },
    { id: createAgentSubfolderNodeId(connectionId, 'operators'), parentId: agentFolder.id, name: 'Operators', type: 'agent-jobs-subfolder', canHaveChildren: true, children: [], isExpanded: false, metadata: { connectionId }, contextMenuActions: SUBFOLDER_CONTEXT_MENU },
    { id: createAgentSubfolderNodeId(connectionId, 'proxies'), parentId: agentFolder.id, name: 'Proxies', type: 'agent-jobs-subfolder', canHaveChildren: true, children: [], isExpanded: false, metadata: { connectionId }, contextMenuActions: SUBFOLDER_CONTEXT_MENU },
    { id: createAgentSubfolderNodeId(connectionId, 'error-logs'), parentId: agentFolder.id, name: 'Error Logs', type: 'agent-jobs-subfolder', canHaveChildren: true, children: [], isExpanded: false, metadata: { connectionId }, contextMenuActions: SUBFOLDER_CONTEXT_MENU },
  ];

  agentFolder.children = subfolders;
  return [agentFolder];
}
